import { useLocation } from 'react-router-dom';
import { useMemo, type ReactNode } from 'react';
import { Breadcrumbs, type Crumb } from '@/components/ui/Breadcrumbs';
import { breadcrumbsForPath } from '@/features/registry';
import { cn } from '@/lib/cn';

interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * Ancestor trail. `true` derives it from the registry `parent` chain; pass an
   * array to override, or omit it on top-level pages that have no ancestors.
   */
  breadcrumbs?: true | Crumb[];
  /**
   * Pin a condensed version of the header to the top of the viewport while the
   * page scrolls. Worth it where the actions matter throughout a long page;
   * elsewhere it just eats vertical space.
   */
  sticky?: boolean;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  breadcrumbs,
  sticky = false,
  className,
}: PageHeaderProps) {
  const { pathname } = useLocation();
  const crumbs = useMemo<Crumb[]>(() => {
    if (!breadcrumbs) return [];
    if (breadcrumbs !== true) return breadcrumbs;
    const trail = breadcrumbsForPath(pathname);
    // The last entry is the page you are already on, so it becomes plain text
    // rather than a link back to itself.
    return trail.map((entry, i) => ({
      label: entry.label,
      to: i === trail.length - 1 ? undefined : entry.path,
    }));
  }, [breadcrumbs, pathname]);

  return (
    <div
      className={cn(
        'mb-4 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-end sm:justify-between',
        // `top-14` clears the Topbar, which is `min-h-14` and sticky itself.
        // The negative margin plus padding lets the backdrop bleed to the edges
        // of the content column instead of leaving a transparent gutter.
        sticky &&
          'sticky top-14 z-20 -mx-3 border-b border-border bg-background/85 px-3 py-3 backdrop-blur sm:-mx-6 sm:px-6',
        className,
      )}
    >
      <div className="min-w-0">
        {crumbs.length > 1 ? <Breadcrumbs items={crumbs} className="mb-1.5" /> : null}
        {eyebrow ? (
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground sm:text-[11px]">
            {eyebrow}
          </p>
        ) : null}
        <h1
          className={cn(
            'font-display font-bold tracking-tight',
            sticky ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl lg:text-3xl',
          )}
        >
          {title}
        </h1>
        {description && !sticky ? (
          <p className="mt-1 max-w-2xl text-xs text-muted-foreground sm:text-sm">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
